// Pure helpers for the [WEBRTC NACK STATS] log (BEAST-13647).
// The log says, per AIQ site, whether Kurento connections had significant NACK (browser asking for
// retransmissions) or were observed long enough without it.

export const NACK_STATS_WINDOW_MS = 10000;
// Normal recovery of single lost packets gives 0-2 NACKs per 10 s; retransmission bursts give hundreds. A rate, because
// windows are longer for offscreen cameras (~30 s sampling) and background tabs (timer throttling).
export const NACK_STATS_MIN_NACKS_PER_SEC = 2;
export const NACK_STATS_NO_NACK_OBSERVATION_MS = 5 * 60 * 1000;

export function pickNackStats(inboundRtp = {}, now = Date.now()) {
    return {
        ssrc: inboundRtp.ssrc,
        timestamp: isNumber(inboundRtp.timestamp) ? inboundRtp.timestamp : now,
        nackCount: inboundRtp.nackCount,
        pliCount: inboundRtp.pliCount,
        packetsLost: inboundRtp.packetsLost,
        packetsReceived: inboundRtp.packetsReceived,
        bytesReceived: inboundRtp.bytesReceived,
        freezeCount: inboundRtp.freezeCount,
        jitter: inboundRtp.jitter,
    };
}

// Counters restart when the receiver changes. packetsLost is not used here: duplicates can legitimately lower it.
export function isNackStatsReset(start, end) {
    return start.ssrc !== end.ssrc || end.packetsReceived < start.packetsReceived || end.bytesReceived < start.bytesReceived;
}

export function summarizeNackStatsWindow(start, end) {
    const windowSec = (end.timestamp - start.timestamp) / 1000;
    if (!(windowSec > 0)) return null;

    const delta = (field) => (isNumber(start[field]) && isNumber(end[field]) ? end[field] - start[field] : undefined);
    const packets = delta('packetsReceived');
    const bytes = delta('bytesReceived');

    return {
        windowSec: Math.round(windowSec * 10) / 10,
        nackDelta: delta('nackCount'),
        pliDelta: delta('pliCount'),
        packetsLostDelta: delta('packetsLost'),
        packetsPerSec: packets === undefined ? undefined : Math.round(packets / windowSec),
        kbps: bytes === undefined ? undefined : Math.round((bytes * 8) / 1000 / windowSec),
        freezeDelta: delta('freezeCount'),
        jitterMs: isNumber(end.jitter) ? Math.round(end.jitter * 1000) : undefined,
    };
}

export function isSignificantNack(summary) {
    return summary.windowSec > 0 && summary.nackDelta / summary.windowSec >= NACK_STATS_MIN_NACKS_PER_SEC;
}

export function formatNackStatsLine(trigger, fields = {}) {
    return `[WEBRTC NACK STATS] ${formatLogFields({ trigger, ...fields })}`;
}

// key=value pairs; strings quoted, empty values skipped
export function formatLogFields(fields = {}) {
    return Object.entries(fields)
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${key}=${typeof value === 'string' ? `"${value}"` : value}`)
        .join(' ');
}

function isNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}
